import { formatInTz } from '../utils/timezone';
import { getApiBaseUrl, getServerUrl } from '../config/apiUrl';

const BASE_URL = getApiBaseUrl();

export const SERVER_URL = getServerUrl();

function getToken() {
  return localStorage.getItem('token');
}

async function apiCall(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    // Token expired or invalid — clear and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    return;
  }

  if (!res.ok) {
    let errMsg = 'Request failed';
    try {
      const errData = await res.json();
      errMsg = errData.error || errData.message || errMsg;
    } catch (_) {}
    throw new Error(errMsg);
  }

  return res.json();
}

// FormData upload (no Content-Type header — browser sets multipart boundary)
async function apiUpload(path, formData) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    return;
  }
  if (!res.ok) {
    let errMsg = 'Upload failed';
    try { const d = await res.json(); errMsg = d.error || d.message || errMsg; } catch (_) {}
    throw new Error(errMsg);
  }
  return res.json();
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => apiCall('/auth/me'),
};

// ─── Tickets ──────────────────────────────────────────────────────────────────
function mapTicket(t) {
  if (!t) return null;
  return {
    id: t.id,
    ticket_number: t.ticket_number || '',
    subject: t.subject || '',
    description: t.description || '',
    requester: t.requester_name || t.requester_email || '',
    email: t.requester_email || '',
    status: t.status || 'Open',
    priority: t.priority || 'Low',
    type: t.type || 'Incident',
    source: t.source || 'Email',
    dept: t.department || '',
    category: t.category || '',
    group: t.assigned_group_name || '',
    assignedTo: t.assigned_to_name || '',
    created: t.created_at
      ? formatInTz(t.created_at, {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true,
        })
      : '',
    modified: t.updated_at
      ? formatInTz(t.updated_at, {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true,
        })
      : '',
    // Keep raw timestamp for relative-time display
    _created_at: t.created_at || null,
    _updated_at: t.updated_at || null,
    // Keep raw API fields for updates
    _assigned_to_id: t.assigned_to || null,
    _assigned_group_id: t.assigned_group || null,
    _requester_id: t.requester_id || null,
    conversations: [],
    starred: t.starred || false,
    tags: t.tags || [],
    // Timer fields
    timer_status:     t.timer_status     || 'stopped',
    timer_elapsed_ms: Number(t.timer_elapsed_ms) || 0,
    timer_started_at: t.timer_started_at || null,
    due_by:           t.due_by || null,
    // Additional categorisation fields
    org_unit:          t.org_unit          || '',
    head_of_department: t.head_of_department || '',
    sub_category:      t.sub_category || t.subcategory || '',
    additional_sub_category: t.additional_sub_category || '',
    end_user:          t.end_user          || '',
    request_rating:    t.request_rating    || null,
    csat_remarks:      t.csat_remarks      || null,
    remarks:           t.remarks           || '',
    procurement_completion_date: t.procurement_completion_date || null,
    reason_for_breach: t.reason_for_breach || '',
    first_response_at: t.first_response_at || null,
    // SLA fields
    slaStatus:          t.sla_status || null,
    resolutionDueAt:    t.resolution_due_at || null,
    firstResponseDueAt: t.first_response_due_at || null,
    // Action taker slots (auto-set by backend on assignment / reply)
    action_taker_1_id:      t.action_taker_1_id      || null,
    action_taker_1_name:    t.action_taker_1_name     || '',
    action_taker_1_remarks: t.action_taker_1_remarks  || '',
    action_taker_1_at:      t.action_taker_1_at       || null,
    action_taker_2_id:      t.action_taker_2_id       || null,
    action_taker_2_name:    t.action_taker_2_name     || '',
    action_taker_2_remarks: t.action_taker_2_remarks  || '',
    action_taker_2_at:      t.action_taker_2_at       || null,
    action_taker_3_id:      t.action_taker_3_id       || null,
    action_taker_3_name:    t.action_taker_3_name     || '',
    action_taker_3_remarks: t.action_taker_3_remarks  || '',
    action_taker_3_at:      t.action_taker_3_at       || null,
    cc: t.requester_cc || '',
    // Latest reply preview (for hover tooltip)
    latestReplyBody:   t.latest_reply_body   || '',
    latestReplySender: t.latest_reply_sender || '',
    latestReplyAt:     t.latest_reply_at     || null,
    latestConversationMode: t.latest_conversation_mode || null,
    latestConversationFromEmail: !!t.latest_conversation_from_email,
    latestConversationSenderId: t.latest_conversation_sender_id ?? null,
    latestConversationSenderEmail: t.latest_conversation_sender_email || '',
  };
}

export const ticketsAPI = {
  list: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    const res = await apiCall(`/tickets${qs ? '?' + qs : ''}`);
    return {
      tickets: (res.tickets || []).map(mapTicket),
      total: res.total || 0,
      page: res.page || 1,
      limit: res.limit || 50,
    };
  },

  get: async (id) => {
    const t = await apiCall(`/tickets/${id}`);
    return mapTicket(t);
  },

  create: async (data) => {
    const t = await apiCall('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapTicket(t);
  },

  update: async (id, data) => {
    const t = await apiCall(`/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return mapTicket(t);
  },

  delete: (id) => apiCall(`/tickets/${id}`, { method: 'DELETE' }),

  timerAction: (id, action, remark) => apiCall(`/tickets/${id}/timer`, {
    method: 'POST',
    body: JSON.stringify({ action, remark }),
  }),

  // Pause request management (manager approval flow)
  getPendingPauseRequests: () => apiCall('/tickets/pause-requests/pending'),
  getLatestPauseRequest: (ticketId) => apiCall(`/tickets/${ticketId}/pause-request/latest`),
  approvePauseRequest: (reqId, note) => apiCall(`/tickets/pause-requests/${reqId}/approve`, {
    method: 'PUT', body: JSON.stringify({ note }),
  }),
  rejectPauseRequest: (reqId, note) => apiCall(`/tickets/pause-requests/${reqId}/reject`, {
    method: 'PUT', body: JSON.stringify({ note }),
  }),
};

// ─── Conversations ────────────────────────────────────────────────────────────
export const conversationsAPI = {
  list: (ticketId) => apiCall(`/tickets/${ticketId}/conversations`),
  create: (ticketId, data, files = []) => {
    if (files && files.length > 0) {
      const fd = new FormData();
      fd.append('mode', data.mode || 'reply');
      fd.append('body', data.body || '');
      if (data.to_email) fd.append('to_email', data.to_email);
      if (data.cc && data.cc.length)  fd.append('cc',  JSON.stringify(data.cc));
      if (data.bcc && data.bcc.length) fd.append('bcc', JSON.stringify(data.bcc));
      files.forEach(f => fd.append('files', f));
      return apiUpload(`/tickets/${ticketId}/conversations`, fd);
    }
    return apiCall(`/tickets/${ticketId}/conversations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const dashboardAPI = {
  stats: () => apiCall('/dashboard/stats'),
  byPriority: () => apiCall('/dashboard/by-priority'),
  byStatus: () => apiCall('/dashboard/by-status'),
  trend: () => apiCall('/dashboard/trend'),
  activity: () => apiCall('/dashboard/activity'),
  leaderboard: () => apiCall('/dashboard/leaderboard'),
  notifications: () => apiCall('/dashboard/notifications'),
  markNotificationRead: (id) =>
    apiCall(`/dashboard/notifications/${id}/read`, { method: 'PUT' }),
  markAllNotificationsRead: () =>
    apiCall('/dashboard/notifications/read-all', { method: 'PUT' }),
  executive: (params = {}) => {
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    const qs = new URLSearchParams(cleaned).toString();
    return apiCall(`/dashboard/executive${qs ? '?' + qs : ''}`);
  },
  execOverview: (params = {}) => {
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    const qs = new URLSearchParams(cleaned).toString();
    return apiCall(`/dashboard/exec-overview${qs ? '?' + qs : ''}`);
  },
};

// ─── Users ────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list: () => apiCall('/users'),
  create: (data) =>
    apiCall('/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiCall(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/users/${id}`, { method: 'DELETE' }),
};

// ─── Groups ───────────────────────────────────────────────────────────────────
export const groupsAPI = {
  list: () => apiCall('/groups'),
  create: (data) =>
    apiCall('/groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiCall(`/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/groups/${id}`, { method: 'DELETE' }),
  members: (id) => apiCall(`/groups/${id}/members`),
  addMember: (groupId, userId) =>
    apiCall(`/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),
  removeMember: (groupId, userId) =>
    apiCall(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
};

// ─── Requester Groups ─────────────────────────────────────────────────────────
export const requesterGroupsAPI = {
  list: () => apiCall('/requester-groups'),
  create: (data) =>
    apiCall('/requester-groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiCall(`/requester-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) =>
    apiCall(`/requester-groups/${id}`, { method: 'DELETE' }),
};

// ─── SLA ──────────────────────────────────────────────────────────────────────
export const slaAPI = {
  list: () => apiCall('/sla'),
  get: (id) => apiCall(`/sla/${id}`),
  create: (data) => apiCall('/sla', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/sla/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/sla/${id}`, { method: 'DELETE' }),
  ticketSla: (ticketId) => apiCall(`/sla/ticket/${ticketId}`),
  refresh: () => apiCall('/sla/refresh', { method: 'POST' }),
};

// ─── SLA Business Calendars ───────────────────────────────────────────────────
export const slaCalendarsAPI = {
  list: () => apiCall('/sla/calendars'),
  create: (data) => apiCall('/sla/calendars', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/sla/calendars/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/sla/calendars/${id}`, { method: 'DELETE' }),
  saveHours: (id, hours) => apiCall(`/sla/calendars/${id}/hours`, { method: 'PUT', body: JSON.stringify({ hours }) }),
  getHolidays: (id) => apiCall(`/sla/calendars/${id}/holidays`),
  addHoliday: (id, data) => apiCall(`/sla/calendars/${id}/holidays`, { method: 'POST', body: JSON.stringify(data) }),
  deleteHoliday: (calId, hid) => apiCall(`/sla/calendars/${calId}/holidays/${hid}`, { method: 'DELETE' }),
};

// ─── SLA Escalation Rules ─────────────────────────────────────────────────────
export const slaEscalationAPI = {
  list: (policyId) => apiCall(`/sla/escalation-rules${policyId ? `?sla_policy_id=${policyId}` : ''}`),
  create: (data) => apiCall('/sla/escalation-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/sla/escalation-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/sla/escalation-rules/${id}`, { method: 'DELETE' }),
};

// ─── SLA Pause Rules ──────────────────────────────────────────────────────────
export const slaPauseAPI = {
  list: () => apiCall('/sla/pause-rules'),
  create: (data) => apiCall('/sla/pause-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/sla/pause-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/sla/pause-rules/${id}`, { method: 'DELETE' }),
};

// ─── SLA Priority Rules ───────────────────────────────────────────────────────
export const slaPriorityAPI = {
  list: () => apiCall('/sla/priority-rules'),
  create: (data) => apiCall('/sla/priority-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/sla/priority-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/sla/priority-rules/${id}`, { method: 'DELETE' }),
};

// ─── SLA Instances (monitoring) ───────────────────────────────────────────────
export const slaInstancesAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined && v !== ''))).toString();
    return apiCall(`/sla/instances${qs ? '?' + qs : ''}`);
  },
};

// ─── Activities ──────────────────────────────────────────────────────────────
export const activitiesAPI = {
  list: (ticketId) => apiCall(`/tickets/${ticketId}/activities`),
};

// ─── Master Data ──────────────────────────────────────────────────────────────
export const masterAPI = {
  all: () => apiCall('/master'),
  list: (fieldType) => apiCall(`/master/${fieldType}`),
  create: (data) => apiCall('/master', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/master/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/master/${id}`, { method: 'DELETE' }),
};

// ─── Reports ──────────────────────────────────────────────────────────────────
export const reportsAPI = {
  summary: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v))).toString();
    return apiCall(`/reports/summary${qs ? '?' + qs : ''}`);
  },
  custom: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v))).toString();
    return apiCall(`/reports/custom${qs ? '?' + qs : ''}`);
  },
};

// ─── Audit Logs ──────────────────────────────────────────────────────────────
const cleanParams = (p) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== '' && v !== null));

export const auditAPI = {
  list: (params = {}) =>
    apiCall('/audit-logs?' + new URLSearchParams(cleanParams(params)).toString()),
  exportUrl: (params = {}) =>
    `${BASE_URL}/audit-logs/export?` + new URLSearchParams(cleanParams(params)).toString(),
};

// ─── Attendance (Holidays / Work Schedule / Leaves) ──────────────────────────
export const attendanceAPI = {
  // Holidays
  listHolidays: (year) => apiCall(`/attendance/holidays${year ? '?year=' + year : ''}`),
  createHoliday: (data) => apiCall('/attendance/holidays', { method: 'POST', body: JSON.stringify(data) }),
  updateHoliday: (id, data) => apiCall(`/attendance/holidays/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteHoliday: (id) => apiCall(`/attendance/holidays/${id}`, { method: 'DELETE' }),
  // Work schedule
  getSchedule: () => apiCall('/attendance/work-schedule'),
  updateSchedule: (items) => apiCall('/attendance/work-schedule', { method: 'PUT', body: JSON.stringify(items) }),
  // Leaves
  listLeaves: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return apiCall(`/attendance/leaves${qs ? '?' + qs : ''}`);
  },
  createLeave: (data) => apiCall('/attendance/leaves', { method: 'POST', body: JSON.stringify(data) }),
  updateLeave: (id, data) => apiCall(`/attendance/leaves/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeave: (id) => apiCall(`/attendance/leaves/${id}`, { method: 'DELETE' }),
  // Summary (scheduled hours / leaves)
  summary: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return apiCall(`/attendance/summary${qs ? '?' + qs : ''}`);
  },
  // Punch in / out
  myStatus: () => apiCall('/attendance/my-status'),
  punchIn:  (notes) => apiCall('/attendance/punch-in',  { method: 'POST', body: JSON.stringify({ notes: notes || null }) }),
  punchOut: (notes) => apiCall('/attendance/punch-out', { method: 'POST', body: JSON.stringify({ notes: notes || null }) }),
  logs: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return apiCall(`/attendance/logs${qs ? '?' + qs : ''}`);
  },
  report: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    ).toString();
    return apiCall(`/attendance/report${qs ? '?' + qs : ''}`);
  },
};

// ─── Workflows ────────────────────────────────────────────────────────────────
export const workflowsAPI = {
  list: () => apiCall('/workflows'),
  create: (data) =>
    apiCall('/workflows', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiCall(`/workflows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/workflows/${id}`, { method: 'DELETE' }),
  logs: (id) => apiCall(`/workflows/${id}/logs`),
  allLogs: (limit = 100) => apiCall(`/workflows/logs/all?limit=${limit}`),
};

// ─── Priority Keywords ────────────────────────────────────────────────────────
export const priorityKeywordsAPI = {
  list:   ()           => apiCall('/priority-keywords'),
  create: (data)       => apiCall('/priority-keywords', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id)         => apiCall(`/priority-keywords/${id}`, { method: 'DELETE' }),
};

// ─── FAQ / Knowledge Base ────────────────────────────────────────────────────
export const faqAPI = {
  // Categories
  listCategories:    ()       => apiCall('/faq/categories'),
  listCategoriesAll: ()       => apiCall('/faq/categories/all'),
  createCategory:    (data)   => apiCall('/faq/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory:    (id, d)  => apiCall(`/faq/categories/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteCategory:    (id)     => apiCall(`/faq/categories/${id}`, { method: 'DELETE' }),
  // FAQs
  list:     (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
    return apiCall(`/faq${qs ? '?' + qs : ''}`);
  },
  get:      (id)         => apiCall(`/faq/${id}`),
  create:   (data)       => apiCall('/faq', { method: 'POST', body: JSON.stringify(data) }),
  update:   (id, data)   => apiCall(`/faq/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete:   (id)         => apiCall(`/faq/${id}`, { method: 'DELETE' }),
  search:   (q, limit)   => apiCall(`/faq/search?q=${encodeURIComponent(q)}${limit ? '&limit=' + limit : ''}`),
  feedback: (id, helpful) => apiCall(`/faq/${id}/feedback`, { method: 'POST', body: JSON.stringify({ helpful }) }),
};

// ─── Password reset (public — company_email → employee_security → OTP → Graph) ─
export const passwordResetAPI = {
  lookup: (companyEmail) =>
    apiCall('/password-reset/lookup', {
      method: 'POST',
      body: JSON.stringify({ companyEmail }),
    }),
  sendOtp: (companyEmail, secretKey) =>
    apiCall('/password-reset/send-otp', {
      method: 'POST',
      body: JSON.stringify({ companyEmail, secretKey }),
    }),
  verifyOtp: (companyEmail, otp) =>
    apiCall('/password-reset/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ companyEmail, otp }),
    }),
  execute: (resetToken) =>
    apiCall('/password-reset/execute', {
      method: 'POST',
      body: JSON.stringify({ resetToken }),
    }),
};

// ─── Employee Security (Admin Reset module) ──────────────────────────────────
export const employeeSecurityAPI = {
  list:   (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/employee-security${qs ? '?' + qs : ''}`);
  },
  get:    (id)       => apiCall(`/employee-security/${id}`),
  create: (data)     => apiCall('/employee-security', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/employee-security/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id)       => apiCall(`/employee-security/${id}`, { method: 'DELETE' }),
};

// ─── M365 allowlist registry (separate from Agent/Requester groups — like Reset) ─
export const m365UsersAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/m365-users${qs ? '?' + qs : ''}`);
  },
  get: (id) => apiCall(`/m365-users/${id}`),
  create: (data) => apiCall('/m365-users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/m365-users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/m365-users/${id}`, { method: 'DELETE' }),
};

export const m365GroupsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/m365-groups${qs ? '?' + qs : ''}`);
  },
  get: (id) => apiCall(`/m365-groups/${id}`),
  create: (data) => apiCall('/m365-groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/m365-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/m365-groups/${id}`, { method: 'DELETE' }),
};

// ─── M365 Automation (Admin approval queue) ───────────────────────────────────
export const automationAPI = {
  health: () => apiCall('/automation/health'),
  getScope: () => apiCall('/automation/admin/scope'),
  listAdminRequests: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/automation/admin/requests${qs ? '?' + qs : ''}`);
  },
  getRequest: (id) => apiCall(`/automation/admin/requests/${id}`),
  approveRequest: (id) =>
    apiCall(`/automation/admin/requests/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  rejectRequest: (id, rejectionReason) =>
    apiCall(`/automation/admin/requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejectionReason: rejectionReason || '' }),
    }),
  submitRequest: (action, params, extra = {}) =>
    apiCall('/automation/requests', {
      method: 'POST',
      body: JSON.stringify({ action, params, ...extra }),
    }),
  /** Admin M365 pages — always queues; never executes Graph without approval */
  submitForApproval: async (action, params, extra = {}) => {
    const result = await apiCall('/automation/requests', {
      method: 'POST',
      body: JSON.stringify({ action, params, channel: 'admin_ui', ...extra }),
    });
    if (!result?.pendingApproval) {
      throw new Error(
        'This action requires CEO/support approval before it runs in Microsoft 365. No changes were made.'
      );
    }
    return result;
  },
  listUsers: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/automation/admin/users${qs ? '?' + qs : ''}`);
  },
  getUser: (userKey) => apiCall(`/automation/admin/users/${encodeURIComponent(userKey)}`),
  listGroups: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiCall(`/automation/admin/groups${qs ? '?' + qs : ''}`);
  },
  getGroupMembers: (groupKey) =>
    apiCall(`/automation/admin/groups/${encodeURIComponent(groupKey)}/members`),
  listLicenses: () => apiCall('/automation/admin/licenses'),
};

// ─── AI Agent chat (PowerDesk backend — no n8n) ─────────────────────────────
export const agentAPI = {
  chat: (message, sessionId, channel = 'chat') =>
    apiCall('/agent/chat', {
      method: 'POST',
      body: JSON.stringify({ message, session_id: sessionId, channel }),
    }),
  voiceToken: (ttl = 3600) =>
    apiCall(`/agent/voice/token?ttl=${encodeURIComponent(ttl)}`),
  clearSession: (sessionId) =>
    apiCall(`/agent/session/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
};
